import { useState, useEffect } from "react";
import { Bell, Zap } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NotificationList } from "./NotificationList";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Notification } from "@servify/shared-schema";

interface NotificationBellProps {
  userId: string;
  userRole: 'admin' | 'employee';
}

export function NotificationBell({ userId, userRole }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [previousCount, setPreviousCount] = useState(0);

  // Fetch unread notifications count
  // Poll more frequently since WebSocket might not be working
  const { data: unreadData, error: unreadError } = useQuery({
    queryKey: ['/notifications/unread'],
    enabled: !!userId,
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    refetchInterval: 1000 * 30, // Poll every 30 seconds (more frequent since WebSocket may not work)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: 2, // Retry failed requests
  });

  // Log errors for debugging
  useEffect(() => {
    if (unreadError) {
      console.error('❌ Failed to fetch notifications:', unreadError);
    }
  }, [unreadError]);

  const unreadCount = (unreadData as { notifications?: Notification[] })?.notifications?.length || 0;

  // Detect new notifications and trigger animation
  useEffect(() => {
    if (unreadCount > previousCount && previousCount > 0) {
      setHasNewNotifications(true);
      // Reset animation after 3 seconds
      setTimeout(() => setHasNewNotifications(false), 3000);
    }
    setPreviousCount(unreadCount);
  }, [unreadCount, previousCount]);

  const handleMarkAllAsRead = async () => {
    try {
      await apiRequest('PUT', '/notifications/read-all');
      queryClient.invalidateQueries({ queryKey: ['/notifications/unread'] });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2 h-11 w-11 rounded-full hover:bg-servify-white-grey"
          data-testid="notification-bell"
        >
          <div className="relative">
            <Bell className={`h-8 w-8 stroke-2 ${unreadCount > 0 ? 'text-gray-800' : 'text-gray-500'}`} />
            
            {/* Notification count badge */}
            {unreadCount > 0 && (
              <span className="absolute -top-[2px] -right-[2px] bg-servify-green text-white font-bold rounded-full h-4 w-4 flex items-center justify-center text-[9px] leading-none border-[1.5px] border-white shadow-sm z-10 translate-x-1/4 -translate-y-1/4">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            
          </div>
          
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`
          w-96 p-0 border-0 shadow-2xl backdrop-blur-lg 
          bg-gradient-to-br from-servify-white-grey/95 to-servify-purple/5 
          rounded-2xl overflow-hidden
        `}
        align="end"
        data-testid="notification-popover"
      >
        {/* Header with gradient background */}
        <div className="p-6 bg-servify-purple text-servify-white-grey">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-servify-white-grey/20 rounded-lg backdrop-blur-sm">
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Notifications</h3>
                <p className="text-servify-white-grey/80 text-sm">
                  {unreadCount > 0 ? `${unreadCount} update${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className={`
                  text-sm text-servify-white-grey/90 hover:text-servify-white-grey hover:bg-servify-white-grey/20 
                  rounded-lg backdrop-blur-sm
                  border border-servify-white-grey/20 px-4 py-2
                `}
                data-testid="mark-all-read"
              >
                Mark all read
              </Button>
            )}
          </div>
        </div>
        <NotificationList 
          userId={userId} 
          userRole={userRole}
          onClose={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}